import './SessionSummarizerSettings.css'
import { useState } from 'react'
import type { z } from 'zod'
import { DEFAULT_SUMMARIZER, SummarizerDefinitionSchema, SummaryDaySchema, summaryLocalDay, type SummarizerCardSchema } from '../schemas/sessionSummarizers'
import { useGetSessionSummarizersQuery, useUpdateSessionSummarizerMutation } from '../services/sessionSummarizersApi'

type Card = z.infer<typeof SummarizerCardSchema>

function SummarizerSettings({ projectId, card, canEdit }: { projectId: string; card: Card; canEdit: boolean }) {
  const [definition, setDefinition] = useState(() => SummarizerDefinitionSchema.parse(card))
  const [day, setDay] = useState(() => new Date(Date.parse(`${summaryLocalDay(card.timezone)}T00:00:00Z`) - 86400000).toISOString().slice(0, 10))
  const [update, request] = useUpdateSessionSummarizerMutation()
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  async function save() {
    setError(null); setMessage(null)
    const parsed = SummarizerDefinitionSchema.safeParse(definition)
    if (!parsed.success) { setError(parsed.error.issues.map(issue => issue.message).join('. ')); return }
    try {
      await update({ projectId, body: { action: 'save', id: card.id, definition: parsed.data } }).unwrap()
      setMessage('Report settings saved.')
    } catch { setError('Could not save report settings. Please try again.') }
  }
  async function generate() {
    setError(null); setMessage(null)
    const parsed = SummaryDaySchema.safeParse(day)
    if (!parsed.success || day >= summaryLocalDay(card.timezone)) { setError('Choose a completed day.'); return }
    try {
      await update({ projectId, body: { action: 'run', id: card.id, day: parsed.data } }).unwrap()
      setMessage(`Summary requested for ${day}.`)
    } catch { setError('Could not request the summary. Please try again.') }
  }
  return <details>
    <summary>{card.name}</summary>
    <form className="session-summarizer-settings__form" onSubmit={event => { event.preventDefault(); void save() }}>
      <label>Name<input required maxLength={100} disabled={!canEdit} value={definition.name} onChange={e => setDefinition({ ...definition, name: e.target.value })} /></label>
      <label>Report instructions<textarea required rows={5} maxLength={20000} disabled={!canEdit} value={definition.prompt} onChange={e => setDefinition({ ...definition, prompt: e.target.value })} /></label>
      <label><input type="checkbox" disabled={!canEdit} checked={definition.enabled} onChange={e => setDefinition({ ...definition, enabled: e.target.checked })} />Generate automatically</label>
      <label>Timezone<input required disabled={!canEdit} value={definition.timezone} onChange={e => setDefinition({ ...definition, timezone: e.target.value })} /></label>
      <label>Hour (0–23)<input type="number" min={0} max={23} required disabled={!canEdit} value={definition.hour} onChange={e => setDefinition({ ...definition, hour: Number(e.target.value) })} /></label>
      {canEdit && <div className="session-summarizer-settings__actions">
        <button disabled={request.isLoading} type="submit">Save report settings</button>
        <label>Report date<input type="date" value={day} onChange={e => setDay(e.target.value)} /></label>
        <button disabled={request.isLoading} type="button" onClick={() => void generate()}>Generate report</button>
      </div>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
    </form>
  </details>
}

export default function SessionSummarizerSettings({ projectId }: { projectId: string }) {
  const query = useGetSessionSummarizersQuery(projectId)
  const [update, request] = useUpdateSessionSummarizerMutation()
  const [error, setError] = useState<string | null>(null)
  async function create() {
    setError(null)
    try {
      await update({ projectId, body: { action: 'save', id: crypto.randomUUID(), definition: DEFAULT_SUMMARIZER } }).unwrap()
    } catch { setError('Could not add a daily report. Please try again.') }
  }
  return <section className="session-summarizer-settings" aria-label="Daily report settings">
    <h3>Daily reports</h3>
    {query.currentData?.can_edit && <button type="button" disabled={request.isLoading} onClick={() => void create()}>Add daily report</button>}
    {error && <p role="alert">{error}</p>}
    {query.isLoading && <p>Loading report settings…</p>}
    {query.isError && <p role="alert">Could not load report settings. <button onClick={() => query.refetch()}>Retry</button></p>}
    {query.currentData?.summarizers.map(card => <SummarizerSettings key={`${projectId}:${card.id}`} projectId={projectId} card={card} canEdit={query.currentData?.can_edit === true} />)}
  </section>
}
